import React, { useEffect, useRef, useState } from "react";
import {
  connectSocket,
  joinRoom,
  sendOffer,
  sendAnswer,
  sendCandidate,
  onOffer,
  onAnswer,
  onCandidate,
} from "../../socket/signalingClient";

export default function VideoCall({ roomId, userId, onClose }) {
  console.log("🎥 VideoCall mounted", { roomId, userId });

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const ringtoneRef = useRef(new Audio("/call-ringtone.mp3"));
  const pendingCandidates = useRef([]);
  const remoteOfferRef = useRef(null);

  const [status, setStatus] = useState("connecting");
  const [role, setRole] = useState("Caller"); // Dynamic role for reverse call

  // Extract original caller/receiver from roomId
  const [originalCallerId, originalReceiverId] = roomId.split("_");
  let isOriginalCaller = userId === originalCallerId;

  useEffect(() => {
    connectSocket(userId);
    joinRoom(roomId, userId);

    // Determine initial role
    if (isOriginalCaller) {
      setRole("Caller");
      setStatus("calling");
    } else {
      setRole("Callee");
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(() => {});
      setStatus("ringing");
    }

    startMedia();

    onOffer(async (offer) => {
      console.log("📩 Offer received");
      remoteOfferRef.current = offer;

      // If media and peer ready, handle offer
      if (localStreamRef.current && pcRef.current) {
        await handleOffer(offer);
      }
    });

    onAnswer(async (answer) => {
      stopRingtone();
      if (pcRef.current) {
        await pcRef.current.setRemoteDescription(
          new RTCSessionDescription(answer)
        );
        setStatus("connected");
      }
    });

    onCandidate(async (candidate) => {
      if (!candidate) return;

      if (pcRef.current) {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } else {
        pendingCandidates.current.push(candidate);
      }
    });

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startMedia() {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    localStreamRef.current = stream;
    localVideoRef.current.srcObject = stream;

    await createPeer();

    // Handle pending remote offer
    if (remoteOfferRef.current) {
      if (role === "Callee") {
        await handleOffer(remoteOfferRef.current);
      }
    } else {
      // If we are initiating a reverse call (not original caller)
      if (!isOriginalCaller) {
        setRole("Caller");
        setStatus("calling");

        const offer = await pcRef.current.createOffer();
        await pcRef.current.setLocalDescription(offer);
        sendOffer(roomId, offer);
      }
    }

    // If original caller, create initial offer
    if (isOriginalCaller) {
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      sendOffer(roomId, offer);
    }
  }

  async function handleOffer(offer) {
    stopRingtone();
    if (!pcRef.current) return;

    await pcRef.current.setRemoteDescription(new RTCSessionDescription(offer));

    const answer = await pcRef.current.createAnswer();
    await pcRef.current.setLocalDescription(answer);
    sendAnswer(roomId, answer);

    setStatus("connected");
  }

  async function createPeer() {
    if (pcRef.current) return;

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:nukkad.vemeet.online:3479" },
        {
          urls: "turn:nukkad.vemeet.online:3479",
          username: "nukkad",
          credential: "MyTurnPass@2025",
        },
      ],
    });

    pcRef.current = pc;

    // Add local tracks
    localStreamRef.current.getTracks().forEach((track) => {
      pc.addTrack(track, localStreamRef.current);
    });

    // Remote stream
    pc.ontrack = (e) => {
      console.log("🎯 Remote stream attached");
      remoteVideoRef.current.srcObject = e.streams[0];
    };

    // ICE candidates
    pc.onicecandidate = (e) => {
      if (e.candidate) sendCandidate(roomId, e.candidate);
    };

    // Add pending candidates
    pendingCandidates.current.forEach((c) =>
      pc.addIceCandidate(new RTCIceCandidate(c))
    );
    pendingCandidates.current = [];
  }

  function stopRingtone() {
    ringtoneRef.current.pause();
    ringtoneRef.current.currentTime = 0;
  }

  function cleanup() {
    stopRingtone();
    pcRef.current?.close();
    pcRef.current = null;

    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
  }

  return (
    <div style={{ padding: 12 }}>
      <h3>
        📹 {role === "Caller" ? "Calling..." : "Incoming Call..."} ({status})
      </h3>

      <div style={{ display: "flex", gap: 10 }}>
        <video
          ref={localVideoRef}
          autoPlay
          muted
          playsInline
          width="240"
          style={{ border: "2px solid #444", borderRadius: 8 }}
        />
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          width="240"
          style={{ border: "2px solid #444", borderRadius: 8 }}
        />
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
        {status === "ringing" && (
          <button
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              backgroundColor: "#4caf50",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
            onClick={async () => {
              stopRingtone();
              setRole("Caller");
              setStatus("calling");

              const offer = await pcRef.current.createOffer();
              await pcRef.current.setLocalDescription(offer);
              sendOffer(roomId, offer);
            }}
          >
            Answer
          </button>
        )}

        <button
          style={{
            padding: "8px 16px",
            borderRadius: 6,
            backgroundColor: "#ff4d4f",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
          onClick={() => {
            cleanup();
            onClose();
          }}
        >
          End Call
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>
        Room ID: {roomId} | User ID: {userId} | Role: {role}
      </div>
    </div>
  );
}
